import { computed, inject } from '@angular/core';
import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  patchState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { ExpertDTO } from '../../../types/expert.types';
import { ExpertApiService } from '../../../services/expert-api.service';
import { TApiErrorResponse } from '@sandbox-platform/shared-ui';

// Store state interface
interface SearchExpertModalState {
  experts: ExpertDTO[];
  searchTerm: string;
  isLoading: boolean;
  error: string | TApiErrorResponse | null;
}

// Initial state
const initialState: SearchExpertModalState = {
  experts: [],
  searchTerm: '',
  isLoading: false,
  error: null,
};

export const SearchExpertModalStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    activeExperts: computed(() =>
      store.experts().filter((expert) => expert.is_active)
    ),
    initialExperts: computed(() =>
      store
        .experts()
        .filter((expert) => expert.is_active)
        .slice(0, 20)
    ),
    hasExperts: computed(() => store.experts().length > 0),
    hasResults: computed(() => {
      const searchTerm = store.searchTerm().trim();
      const activeExperts = store
        .experts()
        .filter((expert) => expert.is_active);
      return searchTerm.length > 0 && activeExperts.length > 0;
    }),
  })),

  withMethods((store) => {
    const expertApiService = inject(ExpertApiService);

    const loadExperts = async (): Promise<void> => {
      patchState(store, { isLoading: true, error: null });

      try {
        const response = await firstValueFrom(
          expertApiService.get({
            is_active: true,
            page: 1,
            limit: 1000, // Load all active experts for search
          })
        );

        if (response?.data?.experts) {
          patchState(store, {
            experts: response.data.experts,
            isLoading: false,
            error: null,
          });
        } else {
          patchState(store, {
            experts: [],
            isLoading: false,
            error: 'No experts data received',
          });
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to load experts';
        patchState(store, {
          experts: [],
          isLoading: false,
          error: errorMessage,
        });
      }
    };

    return {
      // Set search term
      setSearchTerm: (searchTerm: string) => {
        patchState(store, { searchTerm });
      },

      // Clear search
      clearSearch: () => {
        patchState(store, { searchTerm: '', error: null });
      },

      // Clear error
      clearError: () => {
        patchState(store, { error: null });
      },

      // Load experts method
      loadExperts,

      // Reset store to initial state
      reset: () => {
        patchState(store, initialState);
      },
    };
  })
);
